/**
 * Sub-aba Carga — demanda planejada, capacidade e gap.
 * A simulação altera só a leitura deste cenário. Não grava configuração.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { OperationalHeatmap } from '../../OperationalHeatmap';
import { getHeatmapMatrix } from '../../../services/heatmapService';
import type { CapacityConfig, HeatmapFilters } from '../../../services/heatmapService';
import { OpsLoadPeakBar } from '../executive/OpsLoadPeakBar';
import { OpsSimActiveBadge } from '../OpsPanelChrome';
import { CAPACITY_GAP_STATUS_LABEL, type CapacityOutlook } from '../capacityBalance';
import { demandSourceLabel, demandUnitLabel } from '../plannedDemand';

export interface OpsBenchmarkLoadPanelProps {
  heatmapFilters: HeatmapFilters;
  periodLabel: string;
  capacityConfig?: CapacityConfig;
  simulationMultiplier?: number;
  simulationActive?: boolean;
  realOutlook: CapacityOutlook;
  simulatedOutlook?: CapacityOutlook | null;
  simulatedDemandTasksPerHour?: number | null;
  onSimulatedDemandChange?: (value: number | null) => void;
  simulatedTasksPerPersonHour?: number | null;
  onSimulatedProductivityChange?: (value: number | null) => void;
  simulatedAvailableFte?: number | null;
  onSimulatedFteChange?: (value: number | null) => void;
  demandQueryFailed?: boolean;
  demandQueryPending?: boolean;
  onConfigureDemand?: () => void;
}

function fmtTasksPerHour(value: number): string {
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${demandUnitLabel('tasks_per_hour')}`;
}

function fmtFte(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function arrow(real: string, simulated: string): string {
  return real === simulated ? simulated : `${real} → ${simulated}`;
}

export const OpsBenchmarkLoadPanel: React.FC<OpsBenchmarkLoadPanelProps> = ({
  heatmapFilters,
  periodLabel,
  capacityConfig,
  simulationMultiplier,
  simulationActive = false,
  realOutlook,
  simulatedOutlook = null,
  simulatedDemandTasksPerHour = null,
  onSimulatedDemandChange,
  simulatedTasksPerPersonHour = null,
  onSimulatedProductivityChange,
  simulatedAvailableFte = null,
  onSimulatedFteChange,
  demandQueryFailed = false,
  demandQueryPending = false,
  onConfigureDemand,
}) => {
  const shown = simulationActive && simulatedOutlook ? simulatedOutlook : realOutlook;
  const capacityPerHour = shown.capacityTasksPerHour;

  const { data: matrix } = useQuery({
    queryKey: ['heatmap-load-peak', heatmapFilters],
    queryFn: () => getHeatmapMatrix(heatmapFilters),
    staleTime: 60_000,
    enabled: Boolean(heatmapFilters.organizationId),
  });

  const demandText = (outlook: CapacityOutlook) => (
    outlook.demandTasksPerHour == null ? '—' : fmtTasksPerHour(outlook.demandTasksPerHour)
  );
  const capacityText = (outlook: CapacityOutlook) => (
    outlook.capacityTasksPerHour == null ? '—' : fmtTasksPerHour(outlook.capacityTasksPerHour)
  );
  const gapText = (outlook: CapacityOutlook) => (
    outlook.gapTasksPerHour == null ? '—' : fmtTasksPerHour(outlook.gapTasksPerHour)
  );
  const requiredText = (outlook: CapacityOutlook) => (
    outlook.requiredFte == null ? '—' : fmtFte(outlook.requiredFte)
  );
  const fteText = (outlook: CapacityOutlook) => (
    outlook.availableFte == null ? '—' : fmtFte(outlook.availableFte)
  );

  const demandHint = demandQueryFailed
    ? 'Falha ao consultar'
    : demandQueryPending
      ? 'Consultando'
      : shown.demandTasksPerHour == null
        ? 'Não configurada'
        : shown.demandOrigin
          ? demandSourceLabel(shown.demandOrigin)
          : 'Planejada';
  const capacityHint = shown.capacityTasksPerHour != null
    ? `${fmtFte(shown.availableFte ?? 0)} FTE × ${fmtFte(shown.tasksPerPersonHour ?? 0)} tarefa/pessoa/h`
    : shown.blockers.includes('productivity')
      ? 'Produtividade não configurada'
      : 'FTE não configurado';
  const gapHint = shown.gapStatus
    ? CAPACITY_GAP_STATUS_LABEL[shown.gapStatus]
    : shown.blockers.includes('demand')
      ? 'Requer demanda'
      : shown.blockers.includes('productivity')
        ? 'Produtividade não configurada'
        : 'FTE não configurado';
  const requiredHint = shown.requiredFte != null
    ? (shown.fteDeficit != null && shown.fteDeficit > 0 ? `Déficit de FTE ${fmtFte(shown.fteDeficit)}` : shown.fteDeficit === 0 ? 'Sem déficit de FTE' : 'Demanda / produtividade')
    : shown.blockers.includes('demand')
      ? 'Requer demanda'
      : 'Produtividade não configurada';

  const outlook = [
    {
      label: 'Demanda',
      value: simulationActive && simulatedOutlook ? arrow(demandText(realOutlook), demandText(simulatedOutlook)) : demandText(shown),
      hint: simulationActive && simulatedDemandTasksPerHour != null ? 'SIMULADO · não grava' : demandHint,
      tone: 'text-[var(--text-primary)]',
    },
    {
      label: 'Capacidade disponível',
      value: simulationActive && simulatedOutlook ? arrow(capacityText(realOutlook), capacityText(simulatedOutlook)) : capacityText(shown),
      hint: capacityHint,
      tone: 'text-[var(--text-primary)]',
    },
    {
      label: 'Gap',
      value: simulationActive && simulatedOutlook ? arrow(gapText(realOutlook), gapText(simulatedOutlook)) : gapText(shown),
      hint: gapHint,
      tone: shown.gapStatus === 'deficit' ? 'text-red-600' : shown.gapStatus === 'slack' ? 'text-emerald-600' : 'text-[var(--text-primary)]',
    },
    {
      label: 'Déficit de FTE',
      value: shown.fteDeficit == null ? '—' : fmtFte(shown.fteDeficit),
      hint: shown.fteDeficit == null ? 'Requer FTE e produtividade' : 'max(0, necessário − disponível)',
      tone: 'text-[var(--text-primary)]',
    },
    {
      label: 'FTE necessário',
      value: simulationActive && simulatedOutlook ? arrow(requiredText(realOutlook), requiredText(simulatedOutlook)) : requiredText(shown),
      hint: requiredHint,
      tone: 'text-[var(--text-primary)]',
    },
    {
      label: shown.availableFte == null && shown.peopleInScope != null ? 'Pessoas no escopo' : 'FTE disponível',
      value: shown.availableFte == null && shown.peopleInScope != null
        ? String(shown.peopleInScope)
        : (simulationActive && simulatedOutlook ? arrow(fteText(realOutlook), fteText(simulatedOutlook)) : fteText(shown)),
      hint: shown.availableFte == null
        ? 'Contagem de membros. Não é FTE contratual.'
        : shown.peopleInScope != null
          ? `Pessoas no escopo: ${shown.peopleInScope}`
          : 'FTE informado para o cálculo',
      tone: 'text-[var(--text-primary)]',
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-5 pt-2">
        <p className="text-[10px] text-[var(--text-muted)]">
          {simulationActive
            ? 'CENÁRIO SIMULADO · não altera a demanda planejada'
            : 'REAL · demanda planejada, quando existir'}
        </p>
        {simulationActive ? <OpsSimActiveBadge /> : null}
      </div>
      {simulationActive && (
        <div className="mx-5 mt-2 flex flex-wrap items-end gap-3 rounded-[4px] border border-violet-500/30 bg-violet-500/5 px-3 py-2">
          <label className="flex flex-col gap-1 text-[9px] font-semibold uppercase tracking-wider text-violet-300">
            Demanda simulada
            <input
              type="number"
              min={0}
              step={0.1}
              placeholder="—"
              value={simulatedDemandTasksPerHour ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                onSimulatedDemandChange?.(raw === '' ? null : Math.max(0, Number(raw)));
              }}
              className="w-28 rounded-[3px] border border-violet-500/40 bg-[var(--bg-elevated)] px-2 py-1 text-[11px] font-semibold normal-case tracking-normal text-[var(--text-primary)]"
            />
            <span className="font-normal normal-case tracking-normal text-[var(--text-muted)]">vazio usa a planejada · não grava</span>
          </label>
          <label className="flex flex-col gap-1 text-[9px] font-semibold uppercase tracking-wider text-violet-300">
            Produtividade simulada
            <input
              type="number"
              min={0.1}
              step={0.1}
              placeholder="—"
              value={simulatedTasksPerPersonHour ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                onSimulatedProductivityChange?.(raw === '' ? null : Math.max(0.1, Number(raw)));
              }}
              className="w-28 rounded-[3px] border border-violet-500/40 bg-[var(--bg-elevated)] px-2 py-1 text-[11px] font-semibold normal-case tracking-normal text-[var(--text-primary)]"
            />
            <span className="font-normal normal-case tracking-normal text-[var(--text-muted)]">tarefas/pessoa/h · vazio usa a gravada</span>
          </label>
          <label className="flex flex-col gap-1 text-[9px] font-semibold uppercase tracking-wider text-violet-300">
            FTE simulado
            <input
              type="number"
              min={0}
              step={0.1}
              placeholder="—"
              value={simulatedAvailableFte ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                onSimulatedFteChange?.(raw === '' ? null : Math.max(0, Number(raw)));
              }}
              className="w-28 rounded-[3px] border border-violet-500/40 bg-[var(--bg-elevated)] px-2 py-1 text-[11px] font-semibold normal-case tracking-normal text-[var(--text-primary)]"
            />
            <span className="font-normal normal-case tracking-normal text-[var(--text-muted)]">vazio usa o FTE gravado · não grava</span>
          </label>
          <p className="max-w-md text-[9px] leading-relaxed text-[var(--text-muted)]">
            Pessoas no escopo não viram FTE. O fator de carga simulada só altera a contagem observada do mapa. Nada desta caixa é gravado.
          </p>
        </div>
      )}
      <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-[var(--border-default)]/60 px-5 py-2 lg:grid-cols-5">
        {outlook.map((item) => (
          <div key={item.label} className="min-w-0">
            <p className="truncate text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">{item.label}</p>
            <p className={`mt-0.5 text-[13px] font-semibold tabular-nums ${item.tone}`}>{item.value}</p>
            <p className="truncate text-[9px] text-[var(--text-muted)]">{item.hint}</p>
          </div>
        ))}
      </div>
      {!simulationActive && !demandQueryPending && shown.demandTasksPerHour == null && !demandQueryFailed && onConfigureDemand ? (
        <div className="px-5 pb-1">
          <button type="button" onClick={onConfigureDemand} className="text-[10px] font-semibold text-[var(--nav-active-bar)]">
            Configurar em Organização → Capacidade e Demanda
          </button>
        </div>
      ) : null}
      <OpsLoadPeakBar matrix={matrix} capacityPerHour={capacityPerHour} />
      <OperationalHeatmap
        fill
        compact
        maximizeGrid
        title="Mapa de Carga Operacional"
        subtitle={
          capacityPerHour != null
            ? `${periodLabel} · ocupação sobre a capacidade em ${demandUnitLabel('tasks_per_hour')}`
            : simulationMultiplier != null && simulationMultiplier !== 1
              ? `${periodLabel} · cor ajustada pelo fator de carga simulada, não pela demanda planejada`
              : `${periodLabel} · contagem real · capacidade horária não configurada`
        }
        filters={heatmapFilters}
        capacityConfig={capacityConfig}
        simulationMultiplier={simulationMultiplier}
        className="min-h-0 flex-1 border-0 rounded-none"
      />
    </div>
  );
};

export default OpsBenchmarkLoadPanel;
